import React, { useState } from "react";
import { ChevronRight, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

const IMPORTANCE_LEVELS = [
  { id: "non_negotiable", label: "Non-negotiable", description: "Recovery is my top priority in any relationship" },
  { id: "very_important", label: "Very Important", description: "A partner must respect and support my recovery" },
  { id: "important", label: "Important", description: "I value recovery but am open to different perspectives" },
  { id: "flexible", label: "Flexible", description: "I'm in recovery but open to dating outside the community" },
];

const TRIGGERS = [
  { id: "bars", label: "Bars / Nightlife", icon: "🍸" },
  { id: "alcohol_at_home", label: "Alcohol at Home", icon: "🏠" },
  { id: "drug_friendly_spaces", label: "Drug-Friendly Spaces", icon: "🚫" },
  { id: "heavy_drinking_events", label: "Heavy Drinking Events", icon: "🎉" },
  { id: "cannabis_use", label: "Cannabis Use", icon: "🌿" },
  { id: "gambling", label: "Gambling", icon: "🎰" },
];

const RecoveryImportanceStep = ({ onNext }) => {
  const [importance, setImportance] = useState("");
  const [supportPartner, setSupportPartner] = useState(true);
  const [triggers, setTriggers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const toggleTrigger = (triggerId) => {
    setTriggers(prev =>
      prev.includes(triggerId)
        ? prev.filter(t => t !== triggerId)
        : [...prev, triggerId]
    );
  };

  const handleSubmit = async () => {
    if (!importance) {
      toast.error("Please select how important recovery is to you");
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.submitRecoveryImportance({
        recovery_importance: importance,
        willing_to_support_partner: supportPartner,
        triggers_to_avoid: triggers
      });
      onNext(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-2">Recovery Priorities</h2>
      <p className="text-gray-600 mb-6">Help us understand what matters most to you</p>

      {/* Recovery Importance */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          How important is your partner&apos;s understanding of recovery?
        </label>
        <div className="space-y-2">
          {IMPORTANCE_LEVELS.map(level => (
            <button
              key={level.id}
              onClick={() => setImportance(level.id)}
              className={`w-full px-4 py-3 rounded-xl border-2 text-left transition-all ${
                importance === level.id
                  ? 'border-[#1C3A7A] bg-[#1C3A7A]/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <p className={`font-medium ${importance === level.id ? 'text-[#1C3A7A]' : 'text-gray-900'}`}>
                {level.label}
              </p>
              <p className="text-gray-500 text-sm">{level.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Support Partner */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Are you willing to support a partner in their recovery?
        </label>
        <div className="flex gap-3">
          <button
            onClick={() => setSupportPartner(true)}
            className={`flex-1 px-4 py-3 rounded-xl border-2 font-medium transition-all ${
              supportPartner
                ? 'border-[#1C3A7A] bg-[#1C3A7A]/5 text-[#1C3A7A]'
                : 'border-gray-200 hover:border-gray-300 text-gray-700'
            }`}
          >
            Yes, absolutely
          </button>
          <button
            onClick={() => setSupportPartner(false)}
            className={`flex-1 px-4 py-3 rounded-xl border-2 font-medium transition-all ${
              !supportPartner
                ? 'border-[#1C3A7A] bg-[#1C3A7A]/5 text-[#1C3A7A]'
                : 'border-gray-200 hover:border-gray-300 text-gray-700'
            }`}
          >
            Prefer not to
          </button>
        </div>
      </div>

      {/* Triggers */}
      <div className="mb-6">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
          <AlertTriangle className="w-4 h-4 text-[#FAB710]" />
          Select any triggers you need to avoid
        </label>
        <div className="grid grid-cols-2 gap-2">
          {TRIGGERS.map(trigger => (
            <button
              key={trigger.id}
              onClick={() => toggleTrigger(trigger.id)}
              className={`px-3 py-2.5 rounded-xl border-2 text-left transition-all ${
                triggers.includes(trigger.id)
                  ? 'border-red-400 bg-red-50 text-red-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{trigger.icon}</span>
              <span className="text-sm">{trigger.label}</span>
            </button>
          ))}
        </div>
        <p className="text-gray-500 text-xs mt-2">
          We&apos;ll avoid matching you with people whose lifestyle includes these
        </p>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={submitting || !importance}
        className="w-full h-12 bg-[#1C3A7A] hover:bg-[#15305f] text-white font-semibold rounded-xl"
      >
        {submitting ? (
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <>
            Continue
            <ChevronRight className="w-5 h-5 ml-2" />
          </>
        )}
      </Button>
    </div>
  );
};

export default RecoveryImportanceStep;
